<template>
    <div class="px-2 py-4">

        <div class="d-flex justify-space-between align-center flex-wrap sm-gap">
            <div class="font-weight-bold text-darkblue">Parcours</div>
            <div class="text-darkblue">{{ readable_courses }}</div>
        </div>
        <div v-if="get_vague_name" class="d-flex justify-space-between align-center flex-wrap sm-gap">
            <div class="font-weight-bold text-darkblue">Vague</div>
            <div class="text-darkblue">{{ get_vague_name }}</div>
        </div>

        <div v-if="show_equipe_selector" class="d-flex justify-space-between align-center flex-wrap sm-gap mt-2">
            <div class="font-weight-bold text-darkblue">Fais partie de l'équipe</div>
            <v-select :model-value="current_equipe_select_value" :items="equipe_select_items" item-title="title"
                item-value="value" :item-props="equipeItemProps" density="compact" hide-details class="equipe-select"
                @update:model-value="onEquipeChange" />
        </div>

        <v-divider class="mt-2"></v-divider>

        <div class="d-flex justify-end sm-gap flex-wrap">
            <SportipsButton icon="mdi-account-group" v-if="show_equipe_selector" @onClick="scrollToGestionEquipes">
                Gérer l'équipe
            </SportipsButton>
            <SportipsButton icon="mdi-pencil" @onClick="$emit('editInscription')">Modifier</SportipsButton>

            <SportipsButton v-if="show_delete_button" icon="mdi-trash-can" @onClick="openDeleteDialog()">
                Supprimer
            </SportipsButton>
        </div>




    </div>
    <v-dialog width="500" max-width="100%" v-model="deleteDialog" scroll-strategy="none">
        <v-card>
            <v-card-text>
                <div class="text-body-1 text-center">
                    Etes-vous sûr de vouloir supprimer l'inscription de <br />
                    <b>{{ nom_coureur }}</b> ?
                </div>
                <div class="d-flex justify-center flex-wrap gap mt-2">
                    <v-btn color="dark-red" variant="text" @click="closeDeleteDialog()">Non</v-btn>
                    <v-btn color="dark-green" variant="text" @click="deleteInscription()">Oui</v-btn>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@/stores/mainStore';

const CREATE_NEW_EQUIPE_VALUE = '__CREATE_NEW__';

export default {
    props: {
        inscription: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        noDelete: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            deleteDialog: false
        }
    },
    computed: {
        ...mapStores(useMainStore),
        nom_coureur() {
            return this.inscription.get_nom_coureur(this.mainStore.is_default_mode, this.index);
        },
        get_vague_name() {
            return this.inscription.get_vague_name();
        },
        has_selected_courses() {
            return this.inscription.get_courses().length > 0;
        },
        selected_courses() {
            return this.inscription.get_courses();
        },
        readable_courses() {
            if (!this.has_selected_courses) {
                return 'Inscription non sportive';
            }
            return this.selected_courses.map(course => course.nom).join(', ');
        },
        show_delete_button() {
            return !this.mainStore.dossard_bourse_mode && !this.noDelete;
        },
        show_equipe_selector() {
            if (!this.mainStore.module?.need_gestion_equipe) return false;
            return this.selected_courses.some((course) => course.is_equipe_course());
        },
        equipe_course_id() {
            const equipe_course = this.selected_courses.find((course) => course.is_equipe_course());
            return equipe_course?.id ?? null;
        },
        available_equipes() {
            if (!this.equipe_course_id || !this.mainStore.bookingSystem?.equipes?.list) return [];
            return this.mainStore.bookingSystem.equipes.list.filter(
                (equipe) => equipe.id_course == this.equipe_course_id
            );
        },
        current_equipe() {
            const equipesList = this.mainStore.bookingSystem?.equipes?.list ?? [];
            const equipeUid = this.inscription.coureur?.equipe_uid;
            if (equipeUid) {
                return equipesList.find(
                    (equipe) => equipe.uid == equipeUid && equipe.id_course == this.equipe_course_id
                ) ?? null;
            }
            return equipesList.find(
                (equipe) => equipe.id_course == this.equipe_course_id
                    && equipe.coureurs.some((coureur) => coureur.index == this.index)
            ) ?? null;
        },
        current_equipe_select_value() {
            const equipe = this.current_equipe;
            if (!equipe) return '';
            const listIndex = this.mainStore.bookingSystem.equipes.list.indexOf(equipe);
            return equipe.nom || `__index_${listIndex}__`;
        },
        equipe_select_items() {
            const equipesStore = this.mainStore.bookingSystem.equipes;
            const currentEquipe = this.current_equipe;
            const fullList = equipesStore.list;
            const items = this.available_equipes.map((equipe, index) => {
                const listIndex = fullList.indexOf(equipe);
                const size_max = equipe.size_max ?? equipe.size;
                const is_full = !equipesStore._equipe_has_available_spot(equipe);
                const is_current = equipe === currentEquipe;
                const label = equipe.nom || `Equipe n°${index + 1} (sans nom)`;
                const count = `${equipe.coureurs.length}/${size_max}`;
                const title = is_full && !is_current
                    ? `${label} (${count} - complète)`
                    : `${label} (${count})`;
                return {
                    title,
                    value: equipe.nom || `__index_${listIndex}__`,
                    disabled: is_full && !is_current,
                };
            });
            items.push({
                title: 'Créer une nouvelle équipe',
                value: CREATE_NEW_EQUIPE_VALUE,
                disabled: false,
            });
            return items;
        },
    },
    methods: {
        equipeItemProps(item) {
            return {
                disabled: item.disabled,
                class: item.disabled ? 'text-grey' : '',
            };
        },
        onEquipeChange(value) {
            if (!value || value === this.current_equipe_select_value) return;
            const equipesStore = this.mainStore.bookingSystem.equipes;
            if (value === CREATE_NEW_EQUIPE_VALUE) {
                equipesStore.moveCoureurToEquipe(this.index, null);
                return;
            }
            const indexMatch = value.match(/^__index_(\d+)__$/);
            if (indexMatch) {
                equipesStore.moveCoureurToEquipe(this.index, null, parseInt(indexMatch[1], 10));
                return;
            }
            equipesStore.moveCoureurToEquipe(this.index, value);
        },
        openDeleteDialog() {
            this.deleteDialog = true;
        },
        closeDeleteDialog() {
            this.deleteDialog = false;
        },
        deleteInscription() {
            this.$emit('deleteInscription');
            this.closeDeleteDialog();
        },
        scrollToGestionEquipes() {
            this.$nextTick(() => {
                const el = document.getElementById('gestion-equipes');
                if (!el) {
                    return;
                }
                const top = el.getBoundingClientRect().top + window.scrollY - 20;
                window.scrollTo({ top, behavior: 'smooth' });
            });
        },
    }
}
</script>

<style lang="scss" scoped>
.equipe-select {
    max-width: 280px;
    min-width: 180px;
}
</style>